import { locales, defaultLocale } from '../i18n';

const LOCALE_STORAGE_KEY = 'user-locale';

/**
 * Detect the user's preferred locale from browser settings
 * @returns {string} Detected locale or default locale
 */
export const detectBrowserLocale = () => {
  if (typeof window === 'undefined') {
    return defaultLocale;
  }

  // Check if user has previously selected a locale
  const storedLocale = localStorage.getItem(LOCALE_STORAGE_KEY);
  if (storedLocale && locales.includes(storedLocale)) {
    return storedLocale;
  }

  // Get browser languages
  const browserLanguages = navigator.languages || [navigator.language || navigator.userLanguage];

  // Find the first matching locale
  for (const lang of browserLanguages) {
    // Try exact match first (e.g., 'en-US' -> 'en')
    const exactMatch = locales.find(locale => lang.toLowerCase() === locale.toLowerCase());
    if (exactMatch) {
      return exactMatch;
    }

    // Try language code match (e.g., 'en-US' -> 'en')
    const langCode = lang.split('-')[0].toLowerCase();
    const partialMatch = locales.find(locale => locale.toLowerCase() === langCode);
    if (partialMatch) {
      return partialMatch;
    }
  }

  // Return default locale if no match found
  return defaultLocale;
};

/**
 * Save user's locale preference to localStorage
 * @param {string} locale - The locale to save
 */
export const saveLocalePreference = (locale) => {
  if (typeof window !== 'undefined' && locales.includes(locale)) {
    localStorage.setItem(LOCALE_STORAGE_KEY, locale);
  }
};

/**
 * Get stored locale preference
 * @returns {string|null} Stored locale or null
 */
export const getStoredLocale = () => {
  if (typeof window === 'undefined') {
    return null;
  }
  return localStorage.getItem(LOCALE_STORAGE_KEY);
};

/**
 * Clear stored locale preference
 */
export const clearLocalePreference = () => {
  if (typeof window !== 'undefined') {
    localStorage.removeItem(LOCALE_STORAGE_KEY);
  }
};

/**
 * Get locale display name
 * @param {string} locale - The locale code
 * @returns {string} Display name of the locale
 */
export const getLocaleDisplayName = (locale) => {
  const localeNames = {
    en: 'English',
    es: 'Español',
    fr: 'Français',
    de: 'Deutsch',
    pt: 'Português',
    zh: '中文',
    ja: '日本語',
    ar: 'العربية'
  };

  return localeNames[locale] || locale.toUpperCase();
};

/**
 * Check if locale is RTL (Right-to-Left)
 * @param {string} locale - The locale code
 * @returns {boolean} True if locale is RTL
 */
export const isRTL = (locale) => {
  const rtlLocales = ['ar', 'he', 'fa', 'ur'];
  return rtlLocales.includes(locale);
};
